/**
 * EnrolmentStageCard — tells a client where they are in the programme and what
 * happens next (AIMHI Direct AD-03, spec §4.4).
 *
 * Every pre-approval stage resolves to the same portal shell, so without this
 * the shell is identical whether the client needs to consent, needs to book, is
 * waiting for their appointment, or is waiting on their therapist. The stage is
 * the whole message.
 *
 * Copy principles applied here:
 *  - always say what happens next, and who is doing it;
 *  - never leave a waiting stage looking like a stalled one;
 *  - a declined client is not a dropped client (§8.5) — they keep an
 *    explanation, their referral and the crisis panel, phrased so that being
 *    unsuitable for a light-touch pilot does not read as being turned away.
 */

import React from 'react';
import { Card, CardContent, Typography, Button, Stack, Chip, Box, LinearProgress } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

// step index is used for the progress bar; null = not part of the intake run
const STAGES = {
  waitlisted: {
    step: null,
    title: 'You are on the waiting list',
    body: 'There is no appointment free just now. You will be offered one as soon as a place opens, in the order people joined the list. In the meantime you can complete your consent form and questionnaires, so that when a place comes up you can book straight away.',
    tone: 'info'
  },
  pending_consent: {
    step: 0,
    title: 'Start by reading and agreeing the consent form',
    body: 'It explains what the programme involves, what we do with your information, and what this service is and is not. Nothing else opens until this is done.',
    cta: { label: 'Read the consent form', to: '/portal/consent' }
  },
  pending_intake: {
    step: 1,
    title: 'Complete your questionnaires, then book your first appointment',
    body: 'The questionnaires help your therapist understand what you are dealing with before you meet, so the first appointment is spent on you rather than on paperwork.',
    cta: { label: 'Book your first appointment', to: '/appointments/book' },
    secondaryCta: { label: 'Go to questionnaires', to: '/portal/tasks' }
  },
  awaiting_call: {
    step: 2,
    title: 'Your first appointment is booked',
    body: 'You will get a reminder beforehand and a link to join the video call. If you need to change the time, you can cancel and pick another.',
    cta: { label: 'View or change your appointment', to: '/appointments/book' }
  },
  pending_approval: {
    step: 3,
    title: 'Your therapist is reviewing your intake',
    body: 'You have done everything asked of you. Your therapist is confirming the programme is right for you, and your place is held while they do — there is no time limit on this step and nothing further you need to do.',
    tone: 'info'
  },
  // A client who is on /portal when approval lands (or reloads it) stays on
  // the portal: the route does not redirect. Until 9 Sep 2026 that meant no
  // card at all, which read as "still waiting".
  active: {
    step: 4,
    title: 'You have been approved. Your programme starts now',
    body: 'Your therapist has confirmed the programme is right for you. Your AI Supporter has an opening message waiting, and your therapist can read the conversation and is alerted to anything concerning.',
    cta: { label: 'Start with your AI Supporter', to: '/chat' },
    tone: 'success'
  },
  exiting: {
    step: null,
    title: 'You are coming to the end of the programme',
    body: 'There are a few closing questionnaires and a final appointment with your therapist to review how things have gone.',
    cta: { label: 'Book your final appointment', to: '/appointments/book/exit' }
  },
  discharged: {
    step: null,
    title: 'Your programme has finished',
    body: 'Your summary and the resources you can keep using are below. If things change, you can come back to your GP or the services listed here.',
    tone: 'success'
  },
  declined: {
    step: null,
    title: 'This programme is not the right fit',
    body: 'After talking with you, your therapist felt you would be better supported by a different service, and has suggested where to go next. That recommendation is below. This is about matching you to the right level of support, not about turning you away — and the crisis contacts stay available to you at any time.',
    tone: 'warning'
  },
  withdrawn: {
    step: null,
    title: 'You have left the programme',
    body: 'Your place has been released. If you would like to come back, speak to whoever referred you. The crisis contacts remain available to you.',
    tone: 'info'
  },
  lapsed: {
    step: null,
    title: 'Your place has expired',
    body: 'We did not hear back in time, so the place went to the next person waiting. That is not a door closed — get in touch if you would still like to take part.',
    tone: 'warning'
  }
};

const STEP_LABELS = ['Consent', 'Questionnaires', 'Appointment', 'Review'];

/**
 * @param {boolean|null} [intakeComplete] for pending_intake only: false offers
 *   only the questionnaires (8 Sep 2026 tests: clients booked before answering,
 *   so the therapist met them without the forms; since AD-48 the server holds
 *   the booking until they are done).
 *   null/undefined leaves the default order.
 */
export default function EnrolmentStageCard({ status, intakeComplete = null }) {
  let stage = STAGES[status];
  if (!stage) return null;
  if (status === 'pending_intake' && intakeComplete === false) {
    stage = {
      ...stage,
      body: `${stage.body} Booking opens once they are done; they take about ten minutes and your therapist reads them before your call.`,
      cta: stage.secondaryCta,
      secondaryCta: null
    };
  } else if (status === 'pending_intake' && intakeComplete === true) {
    stage = { ...stage, body: `${stage.body} Your questionnaires are done, so the next step is to book your appointment.` };
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        {stage.step !== null && (
          <Box sx={{ mb: 2 }}>
            <Stack direction="row" spacing={1} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
              {STEP_LABELS.map((label, i) => (
                <Chip
                  key={label}
                  size="small"
                  label={label}
                  color={i < stage.step ? 'success' : i === stage.step ? 'primary' : 'default'}
                  variant={i <= stage.step ? 'filled' : 'outlined'}
                />
              ))}
            </Stack>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, (stage.step / (STEP_LABELS.length - 1)) * 100)}
              sx={{ height: 6, borderRadius: 3 }}
            />
          </Box>
        )}

        <Typography variant="h6" gutterBottom>{stage.title}</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: stage.cta ? 2 : 0 }}>
          {stage.body}
        </Typography>

        {(stage.cta || stage.secondaryCta) && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            {stage.cta && (
              <Button variant="contained" component={RouterLink} to={stage.cta.to}>
                {stage.cta.label}
              </Button>
            )}
            {stage.secondaryCta && (
              <Button variant="outlined" component={RouterLink} to={stage.secondaryCta.to}>
                {stage.secondaryCta.label}
              </Button>
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

export { STAGES as ENROLMENT_STAGE_COPY };
